
import { AddVatTuItemsFor } from "@/core/enums/addVatTuItemsFor.enum";
import {
  calcLineAmount,
  formatAmount,
  formatUnitPrice,
} from "@/core/helpers/number-format";
import {
  getErrorMsg,
  instanceOfIDinhMucCoDinhResponseBase,
  instanceOfIVatTuPhieuNhapKhoResponseBase,
  instanceOfIVatTuTrongKhoResponseBase,
  swalNotification,
} from "@/core/helpers/utils";
import { useAccount } from "@/core/hooks/use-account";
import { useButtonCustom } from "@/core/hooks/use-button-custom";
import {
  IDinhMucCoDinhResponseBase,
  IVatTuHopDongResponseBase,
  IVatTuPhieuNhapKho,
  IVatTuResponseBase,
  IVatTuTrongKhoResponseBase,
} from "@/core/interfaces/ApiResponses";
import { KhoService } from "@/core/services/Kho.service";
import { VatTuService } from "@/core/services/VatTu.service";
import { ElInput } from "element-plus";
import { differenceWith } from "lodash";
import { defineComponent, nextTick, PropType, ref } from "vue";

import ButtonCustom from "@/components/shared/buttons/ButtonCustom.vue";
import { FormModalShell } from "@/components/shared/modal";
import useVatTu from "@/core/hooks/use-vat-tu";
import DanhMucToaXeService from "@/core/services/DanhMucToaXe.service";
import DanhMucVatTuService from "@/core/services/DanhMucVatTu.service";
import HopDongVatTuService from "@/core/services/HopDongVatTu.service";
import CurrencyInputCustom from "../forms-elements/CurrencyInputCustom.vue";
import QuantityInputCustom from "../forms-elements/QuantityInputCustom.vue";
import WordBreakColumn from "../shared/work-break-column/WordBreakColumn.vue";
import { VAT_TU_TABLE_COLUMN_WIDTHS } from "@/components/shared/vat-tu-table/vat-tu-table-column-widths";

export interface IVatTuDaChon extends IVatTuResponseBase {
  so_luong: number;
}

const mapVatTuDaChonItem = (item) => {
  const so_luong = instanceOfIDinhMucCoDinhResponseBase(item)
    ? item.pivot.so_luong
    : instanceOfIVatTuPhieuNhapKhoResponseBase(item)
    ? item.pivot.so_luong
    : instanceOfIVatTuTrongKhoResponseBase(item)
    ? item.pivot.so_luong
    : item.so_luong_trong_hop_dong;

  return {
    ...item,
    so_luong: +so_luong,
    don_gia: +(item.don_gia ?? item.pivot?.don_gia ?? 0),
  };
};

export default defineComponent({
  name: "add-vat-tu-items",

  components: {
    ButtonCustom,
    CurrencyInputCustom,
    FormModalShell,
    QuantityInputCustom,
    WordBreakColumn,
  },

  emits: ["close", "added"],

  props: {
    vatTuDaChonItems: {
      type: Array as PropType<
        Array<
          | IDinhMucCoDinhResponseBase
          | IVatTuHopDongResponseBase
          | IVatTuPhieuNhapKho
          | IVatTuTrongKhoResponseBase
        >
      >,
      default: () => [],
    },
    danhMucToaXeId: {
      type: Number,
      default: 0,
    },
    hopDongId: {
      type: Number,
      default: 0,
    },
    khoId: {
      type: Number,
      default: 0,
    },
    isModalOpen: {
      type: Boolean,
      required: true,
    },
    useFor: {
      type: String as PropType<AddVatTuItemsFor>,
      default: AddVatTuItemsFor.DINH_MUC,
    },
  },

  setup() {
    const { ButtonsType, ButtonTypeColors } = useButtonCustom();

    const { currentUserData } = useAccount();

    const vatTuInput = ref<null | typeof ElInput>(null);

    const danhMucVatTuInput = ref<null | typeof ElInput>(null);

    const soLuongInput = ref<null | typeof ElInput>(null);

    const { isTextDangerColumn, isValidLabel } = useVatTu();

    return {
      vatTuInput,
      danhMucVatTuInput,
      soLuongInput,
      currentUserData,
      ButtonsType,
      ButtonTypeColors,
      isTextDangerColumn,
      isValidLabel,
      columnWidths: VAT_TU_TABLE_COLUMN_WIDTHS,
    };
  },

  watch: {
    isModalOpen(value) {
      this.isOpen = value;

      if (value) {
        // After FormModalShell/BaseModal mounts and its auto-focus timer (~80ms).
        nextTick(() => {
          window.setTimeout(() => {
            this.focusVatTuSearch();
          }, 100);
        });
      }
    },

    tenVatTu(value) {
      if (!value) {
        this.isVatTuItemsAvailable = true;
      }
    },

    danhMucVatTuId(id) {
      if (id === 0) {
        this.tenDanhMucVatTu = "";
        return;
      }
    },

    vatTuDaChonItems: {
      handler(items) {
        this.selectedVatTuItems = items.map((item) => mapVatTuDaChonItem(item));
      },
      deep: true,
    },
  },

  data() {
    return {
      AddVatTuItemsFor,
      isOpen: false,
      danhMucVatTuId: 0,
      vatTuId: 0,
      tenDanhMucVatTu: "",
      tenVatTu: "",
      isDanhMucItemsAvailable: true,
      isVatTuItemsAvailable: true,
      so_luong: 1,
      don_gia: null as number | null,
      isProcessing: false,
      selectableVatTuItems: [] as IVatTuResponseBase[],
      selectedVatTuItems: this.vatTuDaChonItems.map((item) =>
        mapVatTuDaChonItem(item)
      ) as IVatTuDaChon[],
    };
  },

  methods: {
    formatUnitPrice,
    formatAmount,
    calcLineAmount,

    getItemSnapshot(
      item,
      fromOriginal = false
    ): {
      id: number;
      so_luong: number;
      don_gia: number;
    } {
      if (fromOriginal) {
        const mapped = mapVatTuDaChonItem(item);
        return {
          id: mapped.id,
          so_luong: +mapped.so_luong,
          don_gia: +mapped.don_gia,
        };
      }

      return {
        id: item.id,
        so_luong: +item.so_luong,
        don_gia: +(item.don_gia ?? 0),
      };
    },

    formatDonGia(value) {
      const numericValue =
        value && typeof value === "object" && "value" in value
          ? value.value
          : value;

      if (numericValue != null && !isNaN(numericValue)) {
        this.don_gia = parseFloat(numericValue);
      }
    },

    focusVatTuSearch() {
      const autocomplete = this.vatTuInput as {
        focus?: () => void;
        getInput?: () => HTMLInputElement | null;
      } | null;

      autocomplete?.focus?.();
      autocomplete?.getInput?.()?.select();
    },

    resetFormFields() {
      this.tenVatTu = "";
      this.vatTuId = 0;
      this.so_luong = 1;
      this.don_gia = null;
      this.danhMucVatTuId = 0;
      this.tenDanhMucVatTu = "";
    },

    onClose() {
      this.isOpen = false;

      this.$emit("close");
    },

    onSelectDanhMucVatTu({ id }) {
      this.danhMucVatTuId = id;

      this.focusVatTuSearch();
    },

    onSelectVatTu({ id }) {
      this.vatTuId = id;

      if (this.soLuongInput?.focus) {
        this.soLuongInput.focus();
      }
    },

    async queryDanhMucVatTu(queryString: string, fn) {
      const {
        data: {
          data: { data },
        },
      } = await DanhMucVatTuService.searchByMaDanhMuc(queryString);

      if (data.length === 0 && queryString) {
        this.isDanhMucItemsAvailable = false;
      } else if (data.length >= 0) {
        this.isDanhMucItemsAvailable = true;
      }

      fn(data);
    },

    async queryVatTu(queryString: string, fn) {
      const {
        data: {
          data: { data: res },
        },
      } = await VatTuService.list({
        id_dm_vat_tu: this.danhMucVatTuId,
        s: queryString,
      });
      const result = differenceWith(
        res,
        this.selectedVatTuItems,
        (newItem, selectedItem: { id: number }) => {
          return newItem.id === selectedItem.id;
        }
      );

      if (queryString && result.length === 0) {
        this.isVatTuItemsAvailable = false;
      } else if (result.length > 0) {
        this.isVatTuItemsAvailable = true;
      }
      fn(result);
    },

    async onAddVatTu() {
      this.isProcessing = true;
      let didAddItem = false;

      try {
        const {
          data: { data: selectingVatTu },
        } = await VatTuService.fetchSupplyDetail(this.vatTuId);

        await nextTick();

        if (selectingVatTu) {
          this.selectedVatTuItems.push({
            ...selectingVatTu,
            so_luong: this.so_luong,
            don_gia:
              this.isForHopDong || this.isForPhieuNhapKho
                ? +(this.don_gia ?? 0)
                : selectingVatTu.don_gia,
          });

          this.resetFormFields();
          didAddItem = true;
        }
      } catch (error) {
        console.error("Error adding vat tu:", error);
      } finally {
        this.isProcessing = false;
      }

      // Focus after re-enabling the search input (disabled while processing).
      if (didAddItem) {
        await nextTick();
        this.focusVatTuSearch();
      }
    },

    onDeleteVatTu(id) {
      this.selectedVatTuItems = this.selectedVatTuItems.filter(
        (item) => item.id !== id
      );
    },

    async submit() {
      this.isProcessing = true;

      try {
        const params = this.selectedVatTuItems.map((item) => ({
          id: item.id,
          so_luong: item.so_luong,
        }));

        if (this.useFor === AddVatTuItemsFor.KHO) {
          try {
            await KhoService.assignVatTuItemVaoKho({
              id_kho: this.khoId,
              vat_tu_items: this.selectedVatTuItems.map(({ id, so_luong }) => ({
                id,
                so_luong,
              })),
            });

            this.resetFormFields();
            this.$emit("added");
          } catch (e) {
            this.isOpen = false;
            await swalNotification(
              getErrorMsg(e, "có lỗi khi lấy thông tin chi tiết đơn vị"),
              "error"
            );
            this.$emit("added");
          }
        }

        if (this.useFor === AddVatTuItemsFor.DINH_MUC) {
          await DanhMucToaXeService.addVatTuVaoDinhMuc(
            this.danhMucToaXeId,
            params
          );
          this.resetFormFields();
          this.$emit("added");
        }

        if (this.useFor === AddVatTuItemsFor.HOP_DONG) {
          await HopDongVatTuService.createHopDongVatTuItems({
            id_hop_dong: this.hopDongId,
            vatTuItems: this.selectedVatTuItems.map((vatTu) => ({
              don_gia: vatTu.don_gia,
              id_vat_tu: vatTu.id,
              so_luong: vatTu.so_luong,
            })),
          });
          this.resetFormFields();
          this.$emit("added");
        }

        if (this.useFor === AddVatTuItemsFor.PHIEU_NHAP_LE) {
          this.$emit("added", {
            vatTuDangChon: [...this.selectedVatTuItems],
          });
        }
      } catch (e) {
        await swalNotification("Có lỗi xảy ra khi thêm vật tư", "error");
        this.resetFormFields();
        this.$emit("close");
      } finally {
        this.isProcessing = false;
      }
    },
  },

  computed: {
    isForAddVatTuKho() {
      return this.useFor === AddVatTuItemsFor.KHO;
    },

    isForHopDong() {
      return this.useFor === AddVatTuItemsFor.HOP_DONG;
    },

    isForPhieuNhapKho() {
      return this.useFor === AddVatTuItemsFor.PHIEU_NHAP_LE;
    },

    titleDisplay() {
      return this.useFor === AddVatTuItemsFor.HOP_DONG
        ? "Danh sách vật tư hợp đồng"
        : this.useFor === AddVatTuItemsFor.PHIEU_NHAP_LE
        ? "Danh sách vật tư nhập kho"
        : this.useFor === AddVatTuItemsFor.KHO
        ? "Danh sách vật tư kho"
        : "Danh sách vật tư định mức";
    },

    isDonGiaInputDisabled() {
      return this.vatTuId === 0 || this.so_luong === 0 || this.isProcessing;
    },

    isAddButtonDisable() {
      return (
        this.vatTuId === 0 ||
        this.so_luong <= 0 ||
        isNaN(this.so_luong) ||
        this.isProcessing
      );
    },

    minDonGia() {
      return this.useFor === AddVatTuItemsFor.PHIEU_NHAP_LE ? 0 : 0.01;
    },

    isChange() {
      if (this.vatTuDaChonItems?.length !== this.selectedVatTuItems.length) {
        return true;
      }

      const originalById = new Map<
        number,
        { id: number; so_luong: number; don_gia: number }
      >(
        this.vatTuDaChonItems.map((item) => [
          item.id,
          this.getItemSnapshot(item, true),
        ])
      );

      return this.selectedVatTuItems.some((item) => {
        const original = originalById.get(item.id);
        if (!original) {
          return true;
        }

        const current = this.getItemSnapshot(item);

        if (original.so_luong !== current.so_luong) {
          return true;
        }

        if (
          (this.isForHopDong || this.isForPhieuNhapKho) &&
          original.don_gia !== current.don_gia
        ) {
          return true;
        }

        return false;
      });
    },

    emptyText() {
      if (this.vatTuDaChonItems?.length === 0) {
        return "Chưa có vật tư nào được chọn";
      }
      return "Bạn đã xóa tất cả vật tư";
    },
  },
});
